import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SEO = ({
  title = "Sicunet - Advanced Access Control Solutions",
  description = "Sicunet provides top-tier access control solutions, including cloud, on-premise, and hybrid systems, along with video surveillance and advanced features like Mifare EV2 credentials and mobile access.",
  keywords = "Cloud access control, On-Premise access control, Hybrid Access Control, Linear Access Control, Nice access control, Access Control, Video Surveillance, Mifare EV2, Mobile Credentials",
  image = "/sicuLogo.png",
  type = "website",
  author = "Sicunet",
  canonical,
  noindex = false,
  nofollow = false,
}) => {
  const location = useLocation();
  const siteUrl = "https://sicunet.com";
  const currentUrl = canonical || `${siteUrl}${location.pathname}`;

  // Structured data for organization
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness"],
    name: "Sicunet",
    url: siteUrl,
    logo: `${siteUrl}/sicuLogo.png`,
    description: description,
    telephone: "857-346-0130",
    address: {
      "@type": "PostalAddress",
      addressCountry: "US",

    },
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "Customer Service",
      telephone: "+857-346-0130",
      email: "sales@sicunet.com",
      url: `${siteUrl}/contact-us`,
      availableLanguage: "English",
    },
    sameAs: [
      "https://www.linkedin.com/company/sicunet-inc/",
      "https://youtube.com/@sicunetsales6249",
    ],
  };

  // WebSite schema with search action
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Sicunet",
    url: siteUrl,
    potentialAction: {
      "@type": "SearchAction",
      target: {
        "@type": "EntryPoint",
        urlTemplate: `${siteUrl}/search?q={search_term_string}`,
      },
      "query-input": "required name=search_term_string",
    },
  };

  // Breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: location.pathname
      .split("/")
      .filter(Boolean)
      .map((path, index, array) => ({
        "@type": "ListItem",
        position: index + 1,
        name: path.charAt(0).toUpperCase() + path.slice(1).replace(/-/g, " "),
        item: `${siteUrl}/${array.slice(0, index + 1).join("/")}`,
      })),
  };

  // Product schema for product pages
  const productSchema =
    type === "product"
      ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: title,
        description: description,
        image: `${siteUrl}${image}`,
        brand: {
          "@type": "Brand",
          name: "Sicunet",
        },
        manufacturer: {
          "@type": "Organization",
          name: "Sicunet",
        },
      }
      : null;

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <link rel="canonical" href={currentUrl} />

      {/* Robots Meta Tags */}
      {(noindex || nofollow) && (
        <meta
          name="robots"
          content={`${noindex ? "noindex" : "index"},${nofollow ? "nofollow" : "follow"
            }`}
        />
      )}

      {/* Hreflang for international SEO */}
      <link rel="alternate" hreflang="en-us" href={currentUrl} />
      <link rel="alternate" hreflang="x-default" href={currentUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={`${siteUrl}${image}`} />
      <meta property="og:site_name" content="Sicunet" />
      <meta property="og:locale" content="en_US" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={currentUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${siteUrl}${image}`} />

      {/* Additional SEO Meta Tags */}
      <meta name="format-detection" content="telephone=no" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />

      {/* Structured Data - Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      {/* Structured Data - WebSite Schema */}
      {location.pathname === "/" && (
        <script type="application/ld+json">
          {JSON.stringify(websiteSchema)}
        </script>
      )}

      {/* Structured Data - Breadcrumb Schema */}
      {location.pathname !== "/" && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}

      {/* Structured Data - Product Schema */}
      {type === "product" && productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
